import { LazyModule, LazyAction, createLazyComponent, registerLazyOrchestrator, type LazyComponent, } from "owa-bundling";
import { orchestrator } from "satcheljs";
import { SearchScenarioId } from "owa-search-store";
import { isCopilotSearchAnswerEnabled } from "owa-copilot-settings-utils";
import { getModuleContextMailboxInfo } from "owa-module-context-mailboxinfo";
import type * as lazyIndex from "./lazyIndex";
import type { CopilotSearchAnswerProps } from "./components/CopilotSearchAnswer";
import { getStore as getMailSearchStore, loadTableViewFromSearchTableQuery } from "owa-mail-search";
import { isSearchPlusChatFeatureEnabledForSearchSession } from "owa-mail-search/lib/utils/isSearchPlusChatFeatureEnabledForSearchSession";
import { endSearchSession, onSearchTextChanged } from "owa-search-actions";

const lazyModule: LazyModule<typeof lazyIndex> = new LazyModule<typeof lazyIndex>(() => import(/* webpackChunkName: "CopilotSearchAnswer" */ "./lazyIndex"), { name: "CopilotSearchAnswer" });

// Lazy component for rendering in search results
export const LazyCopilotSearchAnswer: LazyComponent<{}, unknown, void> = createLazyComponent(lazyModule, (m: typeof lazyIndex) => m.CopilotSearchAnswer);

// Lazy container that displays the copilot answer
export const LazyCopilotSearchAnswerContainer: LazyComponent<CopilotSearchAnswerProps, void> = createLazyComponent(lazyModule, (m: typeof lazyIndex) => m.CopilotSearchAnswerContainer);

// @1js/scc-react-markdown-renderer (loaded via lazyIndex) constructs regexes with negative
// lookbehind at module-eval time, which throws on Safari < 16.4 and fails the chunk load.
// Feature-test here rather than UA-sniff because isBrowserSafari/isMinimumBrowserVersion are
// blocked at module scope by owa-custom-rules/no-side-effects.
// TODO: Remove this gate once @1js/scc-react-markdown-renderer drops the lookbehind regexes.
// Tracked in https://outlookweb.visualstudio.com/Outlook%20Web/_workitems/edit/429095
let supportsRegexLookbehind = false;
try {
    new RegExp("(?<!a)b");
    supportsRegexLookbehind = true;
}
catch {
    // Lookbehind unsupported (Safari < 16.4); leave supportsRegexLookbehind = false.
}

// Prewarm-on-typing entry point. Unlike the start/clear orchestrators, the flight/scenario gate is
// checked in this non-lazy module BEFORE the chunk is imported: onSearchTextChanged fires on every
// keystroke, so registering a lazy orchestrator here would download the CopilotSearchAnswer chunk for
// non-treatment users just from typing. The LazyAction defers the import until the gate passes.
const lazyPrewarmCopilotSearchConnection: LazyAction<typeof lazyIndex.prewarmCopilotSearchConnection, typeof lazyModule> = new LazyAction(lazyModule, m => m.prewarmCopilotSearchConnection);

if (supportsRegexLookbehind) {
    // Run the Copilot answer when a mail search executes.
    registerLazyOrchestrator(loadTableViewFromSearchTableQuery, lazyModule, m => m.startCopilotSearchOrchestrator);
    
    // Pre-open the Sydney socket once the user is typing a natural-language query in the Mail search
    // box.
    // PrewarmConnection is idempotent, so re-firing on subsequent keystrokes is a no-op.
    orchestrator(onSearchTextChanged, actionMessage => {
        const mailSearchStore = getMailSearchStore();
        const mailboxInfo = mailSearchStore.selectedMailboxInfo || getModuleContextMailboxInfo();
        if (actionMessage.scenarioId !== SearchScenarioId.Mail ||
            !isCopilotSearchAnswerEnabled(mailboxInfo)) {
            return;
        }
        if (!isSearchPlusChatFeatureEnabledForSearchSession(actionMessage.scenarioId, mailboxInfo)) {
            return;
        }
        return lazyPrewarmCopilotSearchConnection.importAndExecute(actionMessage.searchText);
    });
    
    // Tear down any Copilot search connection/state when the search session ends.
    registerLazyOrchestrator(endSearchSession, lazyModule, m => m.clearCopilotSearchOrchestrator);
}

// Re-export selectors for checking state
export { getCopilotSearchAnswer, getIsLoading, getIsScrollMinimized } from "./selectors/selectors";

// Re-export mutators that need to be called from outside the lazy chunk
export { setScrollMinimizedMutator } from "./mutators/mutators";
